import { type ReactNode, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAppStore } from '../stores/app-store';
import { useAuthStore } from '../stores/auth-store';
import { useSubscriptionStore } from '../stores/subscription-store';
import { useNotificationsStore } from '../stores/notifications-store';
import { notify, toast } from '../lib/toast';
import { api } from '../lib/api';
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  ArrowLeft,
  Settings,
  LogOut,
  BarChart3,
  X,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Plus,
  FileText,
  BookOpen,
  Heart,
  Quote,
  ListOrdered,
  User,
  GripVertical,
  Trash2,
  Pencil,
  MoreHorizontal,
  Menu,
  Check,
  Settings2,
  CreditCard,
  Sparkles,
  Bell,
} from 'lucide-react';
import { GiQuill, GiBookshelf, GiEnvelope } from './icons/game-icons';
import { NotificationsBell } from './notifications-panel';
import { EditorSettingsMenu } from './editor-settings-menu';
import { UpgradeModal } from './upgrade-modal';
import { GuidedCreateModal } from './guided-create-modal';
import { NewChapterModal } from './new-chapter-modal';
import { AiUsagePill } from './ai-usage-pill';
import { cn } from '../lib/utils';
import { totalWordsOf, writerLevelOf, writerTitleOf } from '../lib/writer-level';
import { DefaultAvatar } from './default-avatar';
import { MomentsWatcher } from './moments';
import { BookCover } from './book-cover';
import { Drawer, DrawerContent, DrawerTitle } from './ui/drawer';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useTheme } from '../hooks/use-theme';
import { ThemeSwitch } from './theme-switch';
import { LangSelect } from './lang-select';
import type { Chapter, SectionType } from '../types';
import { FRONT_MATTER_TYPES, BODY_TYPES, BACK_MATTER_TYPES } from '../types';

// Section icons for the manuscript TOC
const sectionIcons: Record<SectionType, typeof FileText> = {
  'half-title': BookOpen,
  'title-page': BookOpen,
  'copyright': FileText,
  'dedication': Heart,
  'epigraph': Quote,
  'toc': ListOrdered,
  'foreword': MessageSquare,
  'preface': MessageSquare,
  'introduction': FileText,
  'part': BookOpen,
  'prologue': FileText,
  'chapter': FileText,
  'interlude': FileText,
  'epilogue': FileText,
  'afterword': MessageSquare,
  'acknowledgments': Heart,
  'appendix': FileText,
  'glossary': ListOrdered,
  'bibliography': FileText,
  'notes': FileText,
  'about-author': User,
  'also-by': BookOpen,
};

const sectionLabels: Record<SectionType, string> = {
  'half-title': 'Meia Página de Rosto',
  'title-page': 'Página de Rosto',
  'copyright': 'Direitos Autorais',
  'dedication': 'Dedicatória',
  'epigraph': 'Epígrafe',
  'toc': 'Índice',
  'foreword': 'Prefácio do Editor',
  'preface': 'Prefácio',
  'introduction': 'Introdução',
  'part': 'Parte',
  'prologue': 'Prólogo',
  'chapter': 'Capítulo',
  'interlude': 'Interlúdio',
  'epilogue': 'Epílogo',
  'afterword': 'Posfácio',
  'acknowledgments': 'Agradecimentos',
  'appendix': 'Apêndice',
  'glossary': 'Glossário',
  'bibliography': 'Bibliografia',
  'notes': 'Notas',
  'about-author': 'Sobre o Autor',
  'also-by': 'Outros Livros',
};

interface AppShellProps {
  children: ReactNode;
}

// Helper to derive current view from URL.
// bible/outline are no longer in the primary nav; they redirect to the editor workspace.
type AppView = 'dashboard' | 'projects' | 'editor' | 'export' | 'settings' | 'stats' | 'professionals' | 'messages' | 'account' | 'notifications';

function getViewFromPath(pathname: string): AppView {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length <= 1) return 'dashboard'; // /app
  const view = segments[1];
  // Manga uses the same immersive editor shell (top tabs, no app-nav) as books.
  if (view === 'manga') return 'editor';
  const validViews: AppView[] = ['dashboard', 'projects', 'editor', 'export', 'settings', 'stats', 'professionals', 'messages', 'account', 'notifications'];
  return validViews.includes(view as AppView) ? (view as AppView) : 'dashboard';
}

// Mobile bottom-tab button (native-app feel)
function BottomTabButton({
  icon,
  label,
  active,
  onClick,
  badge,
}: {
  icon: ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  badge?: number;
}) {
  return (
    <button
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'relative flex-1 flex flex-col items-center justify-center gap-0.5 touch-manipulation active:scale-95 transition-transform duration-150',
        active ? 'text-primary' : 'text-muted-foreground'
      )}
    >
      <span
        className="relative"
        style={active ? { filter: 'drop-shadow(0 0 8px rgba(223,201,147,0.8))' } : undefined}
      >
        {icon}
        {badge != null && badge > 0 && (
          <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold flex items-center justify-center leading-none">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
      <span className="font-mono text-[8px] uppercase tracking-[0.14em] leading-none">{label}</span>
    </button>
  );
}

export function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { projectId } = useParams<{ projectId?: string }>();
  const {
    projects,
    currentProjectId,
    setCurrentProject,
    currentChapterId,
    setCurrentChapter,
    addChapter,
    deleteChapter,
    updateChapter,
    reorderChapters,
    settings,
    updateSettings,
  } = useAppStore();
  const { user, logout } = useAuthStore();
  const { plan, status, daysRemaining } = useSubscriptionStore();
  const { unreadCount: notifUnreadCount, fetchUnreadCount } = useNotificationsStore();
  useTheme(); // keeps theme applied on mount
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // a carta chega: dispara quando o total de não-lidas SOBE (com folga anti-spam)
  const prevUnreadRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevUnreadRef.current;
    prevUnreadRef.current = unreadCount;
    if (prev === null || unreadCount <= prev) return;
    const last = Number(localStorage.getItem('moment-letter-at') || '0');
    if (Date.now() - last < 5 * 60_000) return;
    localStorage.setItem('moment-letter-at', String(Date.now()));
    window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'letter' } }));
  }, [unreadCount]);
  // notificação chega: dispara quando o total de não-lidas SOBE (com folga anti-spam)
  const prevNotifUnreadRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevNotifUnreadRef.current;
    prevNotifUnreadRef.current = notifUnreadCount;
    if (prev === null || notifUnreadCount <= prev) return;
    const last = Number(localStorage.getItem('moment-notif-at') || '0');
    if (Date.now() - last < 3 * 60_000) return;

    (async () => {
      try {
        const { notifications } = await api.getNotifications();
        const latest = notifications[0];
        if (!latest) return;
        const kind = ({
          collaboration_invite: 'invite',
          project_shared: 'invite',
          mention: 'mention',
          comment: 'mention',
          review: 'review',
          hire_request: 'hire',
          system: 'system',
        } as Record<string, string>)[latest.type];
        if (!kind) return; // 'message' já é coberto pela cena da carta
        localStorage.setItem('moment-notif-at', String(Date.now()));
        window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'notif', kind } }));
      } catch (error) {
        console.error('Failed to fetch latest notification:', error);
      }
    })();
  }, [notifUnreadCount]);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newChapterOpen, setNewChapterOpen] = useState(false);

  // The editor toolbar (a child) opens the chapter TOC drawer via an event.
  useEffect(() => {
    const handler = () => setMobileMenuOpen((o) => !o);
    window.addEventListener('toggle-toc', handler);
    return () => window.removeEventListener('toggle-toc', handler);
  }, []);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close the user menu when clicking outside of it
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [userMenuOpen]);

  // Derive current view from URL
  const currentView = getViewFromPath(location.pathname);

  // Sync projectId from URL with store
  useEffect(() => {
    if (projectId && projectId !== currentProjectId) {
      setCurrentProject(projectId);
    }
  }, [projectId, currentProjectId, setCurrentProject]);

  // Get current project from store or by URL param
  const project = projectId
    ? projects.find((p) => p.id === projectId)
    : projects.find((p) => p.id === currentProjectId);

  // Fetch unread message count
  useEffect(() => {
    const fetchMsgCount = async () => {
      try {
        const response = await api.getUnreadCount();
        setUnreadCount(response.unreadCount);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };

    fetchMsgCount();
    // Poll every 30 seconds
    const interval = setInterval(fetchMsgCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch notification unread count — poll every 60s + on window focus
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);

    const handleFocus = () => fetchUnreadCount();
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchUnreadCount]);
  const isInEditor = currentView === 'editor' && project;
  // Hide the mobile bottom tab bar in immersive views: the editor, and an
  // open conversation thread inside Messages (so the composer gets the full
  // width instead of competing with the nav bar). MessagesView reflects the
  // open conversation via the `c` search param (see messages-view.tsx), so
  // the conversation LIST keeps the tab bar — consistent with Professionals,
  // which has no dedicated back button either.
  const isInOpenConversation = currentView === 'messages' && !!searchParams.get('c');
  const hideMobileTabBar = isInEditor || isInOpenConversation;

  // Current chapter number, in roman numerals — feeds the mobile editor
  // header ("‹ Título · Cap. XII · ⚙"). Only counts 'chapter' sections
  // (front/back matter has no chapter number), same rule as the TOC below.
  const currentChapterRoman = (() => {
    if (!isInEditor || !currentChapterId) return null;
    let idx = 0;
    for (const c of project.chapters) {
      if (c.sectionType !== 'chapter') continue;
      idx++;
      if (c.id === currentChapterId) return tocRoman(idx);
    }
    return null;
  })();

  const userInitials =
    user?.name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';

  // Nível do escritor — alimenta o rail-user da casca (demo Odisseia)
  const writerLevel = writerLevelOf(totalWordsOf(projects));
  const writerTitle = writerTitleOf(writerLevel.level, t);

  // Título da tela no win-top da casca (mock: "REFORMU.LAT — INÍCIO")
  const currentViewLabel = ({
    dashboard: t('nav.dashboard', 'Início'),
    projects: t('nav.library', 'Biblioteca'),
    messages: t('nav.messages', 'Mensagens'),
    professionals: t('nav.professionals', 'Profissionais'),
    notifications: t('nav.notifications', 'Notificações'),
    stats: t('nav.stats', 'Estatísticas'),
    settings: t('nav.settings', 'Configurações'),
    account: t('nav.account', 'Conta'),
  } as Record<string, string>)[currentView as string] ?? '';

  const handleNavClick = (view: AppView) => {
    const routes: Record<AppView, string> = {
      dashboard: '/app',
      projects: '/app/projects',
      editor: project ? `/app/editor/${project.id}` : '/app/projects',
      export: project ? `/app/export/${project.id}` : '/app/projects',
      settings: '/app/settings',
      stats: '/app/stats',
      professionals: '/app/professionals',
      messages: '/app/messages',
      account: '/app/account',
      notifications: '/app/notifications',
    };
    navigate(routes[view]);
    setMobileMenuOpen(false);
  };

  // Drag and drop sensors - use delay for better click vs drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 150, // 150ms hold before drag starts
        tolerance: 5, // 5px movement allowed during delay
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // Slightly longer for touch
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent, chapters: Chapter[]) => {
    const { active, over } = event;

    if (over && active.id !== over.id && project) {
      const oldIndex = chapters.findIndex((c) => c.id === active.id);
      const newIndex = chapters.findIndex((c) => c.id === over.id);
      reorderChapters(project.id, oldIndex, newIndex);
    }
  };

  const handleDeleteChapter = async (chapterId: string) => {
    if (!project) return;
    const chapter = project.chapters.find((c) => c.id === chapterId);

    toast(t('editor.deleteChapterConfirm', 'Excluir este capítulo?'), {
      description: chapter?.title,
      action: {
        label: t('common.delete'),
        onClick: async () => {
          await deleteChapter(project.id, chapterId);
          // cerimônia: uma página sai da história
          window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'tearpage' } }));
          if (currentChapterId === chapterId) {
            const remaining = project.chapters.filter((c) => c.id !== chapterId);
            if (remaining.length > 0) {
              setCurrentChapter(remaining[0].id);
            }
          }
          notify.deleted(chapter?.title || t('sections.chapter'));
        },
      },
      cancel: {
        label: t('common.cancel'),
        onClick: () => {},
      },
      duration: 10000,
    });
  };

  const handleRenameChapter = (chapterId: string, newTitle: string) => {
    if (!project) return;
    updateChapter(project.id, chapterId, { title: newTitle });
  };

  // Categorize chapters into groups
  const frontMatter = project?.chapters
    .filter((c) => FRONT_MATTER_TYPES.includes(c.sectionType))
    .sort((a, b) => FRONT_MATTER_TYPES.indexOf(a.sectionType) - FRONT_MATTER_TYPES.indexOf(b.sectionType)) || [];

  const bodyContent = project?.chapters.filter((c) => BODY_TYPES.includes(c.sectionType)) || [];

  const backMatter = project?.chapters
    .filter((c) => BACK_MATTER_TYPES.includes(c.sectionType))
    .sort((a, b) => BACK_MATTER_TYPES.indexOf(a.sectionType) - BACK_MATTER_TYPES.indexOf(b.sectionType)) || [];

  // State for section expansion
  const [expandedSections, setExpandedSections] = useState({ front: false, body: true, back: false });

  const handleToggleFrontMatter = (type: SectionType) => {
    if (!project) return;
    const existing = frontMatter.find((c) => c.sectionType === type);
    if (existing) {
      deleteChapter(project.id, existing.id);
    } else {
      addChapter(project.id, sectionLabels[type], type);
    }
  };

  const handleToggleBackMatter = (type: SectionType) => {
    if (!project) return;
    const existing = backMatter.find((c) => c.sectionType === type);
    if (existing) {
      deleteChapter(project.id, existing.id);
    } else {
      addChapter(project.id, sectionLabels[type], type);
    }
  };

  const handleAddBodyChapter = (type: SectionType) => {
    if (!project) return;
    const chapterCount = bodyContent.filter((c) => c.sectionType === 'chapter').length;
    const title = type === 'chapter' ? `Capítulo ${chapterCount + 1}` : sectionLabels[type];
    addChapter(project.id, title, type);
  };

  // Primary nav: always visible in the sidebar and mobile bottom bar.
  const primaryNavItems = [
    { id: 'dashboard' as AppView, icon: <LayoutDashboard size={20} />, label: t('nav.dashboard', 'Início') },
    { id: 'projects' as AppView, icon: <GiBookshelf size={20} />, label: t('nav.projects', 'Estante') },
    { id: 'messages' as AppView, icon: <GiEnvelope size={20} />, label: t('nav.messages', 'Mensagens'), badge: unreadCount },
  ];

  // Secondary nav: shown in the desktop sidebar "Mais" section and the mobile drawer.
  // bible/outline are not here — they live inside the book workspace (editor tabs).
  const moreNavItems = [
    { id: 'professionals' as AppView, icon: <Users size={20} />, label: t('nav.professionals', 'Profissionais') },
    { id: 'notifications' as AppView, icon: <Bell size={20} />, label: t('nav.notifications', 'Notificações'), badge: notifUnreadCount },
  ];

  return (
    <div className="app-casca h-dvh w-full flex flex-col overflow-hidden bg-background text-foreground">
      {/* ── win-top: o topo da casca do mock — por cima da sidebar, sempre ── */}
      <div className="win-top hidden md:flex">
        {isInEditor ? (
          <>
            {/* voltar pros projetos vive aqui, como no mock */}
            <button
              onClick={() => navigate('/app/projects')}
              className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground transition-colors"
              title={t('nav.backToProjects', 'Voltar para Projetos')}
            >
              ‹ {t('nav.library', 'Biblioteca')}
            </button>
            {/* abas Editor · Universo · Estrutura — centradas na casca */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1">
              {([
                { ws: null, label: t('editorView.tabEditor', 'Editor') },
                { ws: 'bible', label: t('editorView.tabUniverse', 'Universo') },
                { ws: 'outline', label: t('editorView.tabStructure', 'Estrutura') },
              ] as const).map(({ ws, label }) => {
                const cur = searchParams.get('ws');
                const active = (cur === 'bible' && ws === 'bible') || (cur === 'outline' && ws === 'outline') || (!cur && ws === null) || (cur !== 'bible' && cur !== 'outline' && ws === null);
                return (
                  <button
                    key={label}
                    onClick={() => {
                      const next = new URLSearchParams(searchParams);
                      if (ws) next.set('ws', ws); else next.delete('ws');
                      setSearchParams(next, { replace: true });
                    }}
                    className={cn(
                      'px-[17px] py-2 rounded-lg font-mono text-[10px] uppercase tracking-[0.2em] transition-colors',
                      active
                        ? 'text-[color:var(--ember-hot)] bg-[rgba(223,201,147,0.08)] shadow-[0_0_0_1px_rgba(223,201,147,0.28)_inset]'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <span className="top-title">
            <motion.span
              key={currentViewLabel}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="inline-block"
            >
              reformu.lat{currentViewLabel ? ` — ${currentViewLabel}` : ''}
            </motion.span>
          </span>
        )}
        <div className="win-icons">
          <button
            onClick={() => handleNavClick('settings')}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground transition-colors"
            title={t('nav.settings', 'Configurações')}
            aria-label={t('nav.settings', 'Configurações')}
          >
            <Settings size={14} />
          </button>
        </div>
      </div>
      <div className="flex-1 flex min-h-0 w-full overflow-hidden">
      {/* Desktop Sidebar — o "rail" da casca Odisseia: estreito, mais escuro
          que os cards, itens mono e o usuário com nível embaixo.
          No editor de MANGÁ some por completo: o mangá tem o próprio painel
          lateral (Roteiro) — só a casca imersiva, sem app-nav nem TOC. */}
      {!(isInEditor && project.kind === 'manga') && (
      <aside
        className="hidden md:flex w-[232px] flex-col flex-shrink-0 border-r border-border bg-transparent"
      >
        {/* Brand / Logo — só fora do editor; no editor o sumário começa no livro */}
        {!isInEditor && (
        <div className="h-16 flex items-center justify-between gap-2 px-4 flex-shrink-0">
          <button
            onClick={() => handleNavClick('dashboard')}
            className="flex items-center gap-2 min-w-0 hover:opacity-80 transition-opacity"
          >
            {/* Logo image — light/dark swap */}
            <img
              src="/log-black.png"
              alt="reformu.lat"
              className="h-5 shrink-0 dark:hidden"
            />
            <img
              src="/log-white.png"
              alt="reformu.lat"
              className="h-5 shrink-0 hidden dark:block"
            />
            <span className="font-mono text-sm font-bold text-foreground tracking-tight truncate">
              reformu<span className="text-accent">.</span>lat
            </span>
          </button>
        </div>
        )}

        {/* Main Navigation or Manuscript TOC */}
        <div className="flex-1 overflow-y-auto">
          {isInEditor ? (
            // Manuscript TOC when editing - organized by section
            <div className="p-2 space-y-3">
              {/* toc-book — cartão do livro com capa mini + progresso + nível */}
              <div className="flex items-center gap-2.5 mx-1 px-2.5 py-2 rounded-[10px] bg-card shadow-[0_0_0_1px_var(--border)_inset]">
                <div className="shrink-0 w-6">
                  <BookCover
                    title={project.metadata.title || t('projects.untitled', 'Sem título')}
                    author={project.metadata.author || ''}
                    genre={project.metadata.genre || 'Fiction'}
                    coverImage={project.metadata.coverImage}
                    variant="cover"
                    progress={0}
                    seed={project.id}
                    disableTilt
                    className="w-6"
                  />
                </div>
                <div className="min-w-0">
                  <h2 className="font-serif text-[12.5px] font-medium leading-tight text-foreground truncate">
                    {project.metadata.title || t('projects.untitled', 'Sem título')}
                  </h2>
                  <p className="font-mono text-[7.5px] uppercase tracking-[0.14em] text-muted-foreground mt-0.5">
                    {Math.min(100, Math.round((totalWordsOf([project]) / 80000) * 100))}% · {t('hud.lvlAbbrev', 'Nv')} {writerLevel.level}
                  </p>
                </div>
              </div>

              {/* Manga has no chapters — its pages live in the canvas editor. */}
              {project.kind !== 'manga' && (<>
              {/* FRONT MATTER - Fixed order, not draggable */}
              <div className="rounded-lg p-1">
                <div className="flex items-center justify-between px-1 py-1 mb-1">
                  <button
                    onClick={() => setExpandedSections((p) => ({ ...p, front: !p.front }))}
                    className="flex items-center gap-1.5 font-mono text-[8.5px] font-medium text-muted-foreground uppercase tracking-[0.24em] hover:text-foreground"
                  >
                    {expandedSections.front ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                    {t('appShell.frontMatter', 'Pré-Textuais')}
                    {frontMatter.length > 0 && (
                      <span className="ml-1 text-[9px] bg-muted px-1 rounded">{frontMatter.length}</span>
                    )}
                  </button>
                  <SectionFilterPopup
                    title={t('appShell.frontMatter', 'Pré-Textuais')}
                    types={FRONT_MATTER_TYPES}
                    enabledTypes={frontMatter.map((c) => c.sectionType)}
                    onToggle={handleToggleFrontMatter}
                  />
                </div>
                <AnimatePresence initial={false}>
                  {expandedSections.front && frontMatter.length > 0 && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-0.5">
                        {frontMatter.map((chapter) => (
                          <FixedChapterItem
                            key={chapter.id}
                            chapter={chapter}
                            isActive={currentChapterId === chapter.id}
                            onSelect={() => setCurrentChapter(chapter.id)}
                            onDelete={() => handleDeleteChapter(chapter.id)}
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                {expandedSections.front && frontMatter.length === 0 && (
                  <p className="text-[10px] text-muted-foreground/60 px-2 py-2 text-center italic">
                    {t('appShell.clickIconToAdd', 'Clique no ícone para adicionar')}
                  </p>
                )}
              </div>

              {/* BODY - Draggable chapters */}
              <div className="rounded-lg p-1">
                <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 px-1 py-1 mb-1">
                  <button
                    onClick={() => setExpandedSections((p) => ({ ...p, body: !p.body }))}
                    className="flex items-center gap-1.5 whitespace-nowrap font-mono text-[8.5px] font-medium text-muted-foreground uppercase tracking-[0.24em] hover:text-foreground"
                  >
                    {expandedSections.body ? <ChevronDown size={10} className="shrink-0" /> : <ChevronRight size={10} className="shrink-0" />}
                    <BookOpen size={10} className="shrink-0" />
                    {t('appShell.yourStory', 'Sua História')}
                    <span className="ml-1 text-[9px] bg-primary/20 px-1 rounded">{bodyContent.length}</span>
                  </button>
                  {expandedSections.body && (
                    <span className="whitespace-nowrap text-[9px] text-primary/50 font-medium">{t('appShell.dragToReorder', 'arraste para reordenar')}</span>
                  )}
                </div>

                <AnimatePresence initial={false}>
                  {expandedSections.body && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(e) => handleDragEnd(e, bodyContent)}
                      >
                        <SortableContext
                          items={bodyContent.map((c) => c.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-0.5">
                            {(() => {
                              let chapterNum = 0;
                              return bodyContent.map((chapter) => {
                                if (chapter.sectionType === 'chapter') chapterNum++;
                                return (
                                  <SortableChapterItem
                                    key={chapter.id}
                                    chapter={chapter}
                                    chapterNumber={chapter.sectionType === 'chapter' ? chapterNum : undefined}
                                    isActive={currentChapterId === chapter.id}
                                    onSelect={() => setCurrentChapter(chapter.id)}
                                    onDelete={() => handleDeleteChapter(chapter.id)}
                                    onRename={(title) => handleRenameChapter(chapter.id, title)}
                                  />
                                );
                              });
                            })()}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* New chapter — opens the unified create modal (Vazio / Com IA) */}
                <button
                  type="button"
                  onClick={() => setNewChapterOpen(true)}
                  className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-foreground/25 p-2.5 font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary"
                >
                  <Plus size={14} />
                  {t('editor.addChapter', 'Novo capítulo')}
                </button>
              </div>

              {/* BACK MATTER - Fixed order, not draggable */}
              <div className="rounded-lg p-1">
                <div className="flex items-center justify-between px-1 py-1 mb-1">
                  <button
                    onClick={() => setExpandedSections((p) => ({ ...p, back: !p.back }))}
                    className="flex items-center gap-1.5 font-mono text-[8.5px] font-medium text-muted-foreground uppercase tracking-[0.24em] hover:text-foreground"
                  >
                    {expandedSections.back ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                    {t('appShell.backMatter', 'Pós-Textuais')}
                    {backMatter.length > 0 && (
                      <span className="ml-1 text-[9px] bg-muted px-1 rounded">{backMatter.length}</span>
                    )}
                  </button>
                  <SectionFilterPopup
                    title={t('appShell.backMatter', 'Pós-Textuais')}
                    types={BACK_MATTER_TYPES}
                    enabledTypes={backMatter.map((c) => c.sectionType)}
                    onToggle={handleToggleBackMatter}
                  />
                </div>
                <AnimatePresence initial={false}>
                  {expandedSections.back && backMatter.length > 0 && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-0.5">
                        {backMatter.map((chapter) => (
                          <FixedChapterItem
                            key={chapter.id}
                            chapter={chapter}
                            isActive={currentChapterId === chapter.id}
                            onSelect={() => setCurrentChapter(chapter.id)}
                            onDelete={() => handleDeleteChapter(chapter.id)}
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                {expandedSections.back && backMatter.length === 0 && (
                  <p className="text-[10px] text-muted-foreground/60 px-2 py-2 text-center italic">
                    {t('appShell.clickIconToAdd', 'Clique no ícone para adicionar')}
                  </p>
                )}
              </div>
              </>)}
            </div>
          ) : (
            // Regular navigation — o rail da demo: lista única, sem seções
            <nav className="p-3 space-y-0.5">
              {[
                ...primaryNavItems.map((item) =>
                  item.id === 'projects'
                    ? { ...item, label: t('nav.library', 'Biblioteca') }
                    : item
                ),
                ...moreNavItems,
              ].map((item) => {
                // símbolos do rail do mock (◈ ▤ ✉ ◎ ◔ ♪)
                const sym = ({
                  dashboard: '◈', projects: '▤', messages: '✉',
                  professionals: '◎', notifications: '◔', stats: '♪',
                } as Record<string, string>)[item.id as string];
                return (
                  <NavItem
                    key={item.id}
                    icon={sym ? <i className="not-italic text-[14px] leading-none">{sym}</i> : item.icon}
                    label={item.label}
                    badge={item.badge}
                    active={currentView === item.id}
                    onClick={() => handleNavClick(item.id)}
                  />
                );
              })}

              {/* Planos — item do rail (mock), abre o carrossel de planos */}
              <NavItem
                icon={<i className="not-italic text-[14px] leading-none">❖</i>}
                label={t('nav.plans', 'Planos')}
                active={false}
                onClick={() => setUpgradeOpen(true)}
              />
            </nav>
          )}
        </div>

        {/* Bottom Section */}
        <div className="p-3 space-y-2.5 flex-shrink-0">

          {/* Upgrade card — opens the in-app upgrade modal (no full-page nav).
              Horizontal layout (badge · copy · chevron) with a hover sheen. */}
          {(plan === 'free' || status === 'trialing') && (
            <button
              onClick={() => setUpgradeOpen(true)}
              className="group relative w-full overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-card to-accent/10 p-2.5 text-left transition-all duration-300 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10"
            >
              {/* sheen sweep on hover */}
              <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/12 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />
              <div className="relative flex items-center gap-3">
                <Sparkles
                  size={22}
                  className="shrink-0 text-primary transition-transform duration-300 group-hover:scale-110"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold leading-tight text-foreground">
                    {status === 'trialing'
                      ? t('subscription.trialEnds', 'Teste: {{days}}d restantes', { days: daysRemaining })
                      : t('appShell.elevateWriting', 'Eleve sua escrita')}
                  </p>
                  <p className="mt-0.5 text-[10px] leading-tight text-muted-foreground">
                    {status === 'trialing'
                      ? t('subscription.unlockAll', 'Garanta o acesso completo')
                      : t('appShell.aiCoversTools', 'IA, capas e ferramentas pro')}
                  </p>
                </div>
                <ChevronRight
                  size={15}
                  className="shrink-0 text-primary/60 transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-primary"
                />
              </div>
            </button>
          )}

          {/* AI usage indicator — slim, always-visible bar (moved out of the editor) */}
          <AiUsagePill variant="bar" />

          {/* User block — clicking toggles an animated popup menu above (no layout shift) */}
          <div className="relative" ref={userMenuRef}>
            <AnimatePresence>
              {userMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="absolute bottom-full left-0 right-0 z-50 mb-2 origin-bottom rounded-xl border border-border bg-popover p-1 shadow-lg ring-1 ring-foreground/10"
                >
                  <UserMenuItem
                    icon={<User size={15} />}
                    label={t('nav.account', 'Conta')}
                    onClick={() => { handleNavClick('account'); setUserMenuOpen(false); }}
                  />
                  <UserMenuItem
                    icon={<Settings size={15} />}
                    label={t('nav.settings', 'Configurações')}
                    onClick={() => { handleNavClick('settings'); setUserMenuOpen(false); }}
                  />
                  <div className="my-1 h-px bg-border" />
                  {/* Theme + language — the real custom switcher + flag picker */}
                  <div className="flex items-center justify-between gap-2 rounded-lg px-2.5 py-1">
                    <span className="text-sm text-foreground">{t('settings.theme', 'Tema')}</span>
                    <ThemeSwitch sz={20} />
                  </div>
                  <div className="flex items-center justify-between gap-2 rounded-lg px-2.5 py-1">
                    <span className="text-sm text-foreground">{t('settings.language', 'Idioma')}</span>
                    <LangSelect />
                  </div>
                  <div className="my-1 h-px bg-border" />
                  <UserMenuItem
                    icon={<LogOut size={15} />}
                    label={t('auth.logout', 'Sair')}
                    destructive
                    onClick={() => { logout(); setUserMenuOpen(false); }}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* rail-user do mock — avatar 30px + nome + "Nv 7 · Romancista";
                o clique continua abrindo o menu (conta/tema/idioma/sair) */}
            <button
              onClick={() => setUserMenuOpen((o) => !o)}
              className={cn('rail-user-mock w-full text-left touch-manipulation transition-[filter]', userMenuOpen && 'brightness-125')}
            >
              <span className="w-[30px] h-[30px] rounded-full overflow-hidden shrink-0 grid place-items-center">
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <DefaultAvatar name={user?.id || user?.email || user?.name || 'eu'} />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <b className="truncate">{user?.name}</b>
                <small>{t('hud.lvlAbbrev', 'Nv')} {writerLevel.level} · {writerTitle}</small>
              </span>
            </button>
          </div>

        </div>
      </aside>
      )}

      {/* In-app upgrade modal (Dialog desktop / Drawer mobile) — opened from the
          sidebar upgrade card and anywhere else that needs it. */}
      {/* cenas-recompensa: vela · level up · amanhecer */}
      <MomentsWatcher />

      <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} />

      {/* New-book flow opened by the center "+" of the mobile tab bar (no
          redundant navigation — the sheet opens right over the current screen). */}
      <GuidedCreateModal open={createOpen} onOpenChange={setCreateOpen} />

      {/* Unified new-chapter modal (Vazio / Com IA) — only available while in a project */}
      {project && (
        <NewChapterModal
          open={newChapterOpen}
          onOpenChange={setNewChapterOpen}
          projectId={project.id}
          chapters={project.chapters}
          onCreated={(id) => {
            setCurrentChapter(id);
            setMobileMenuOpen(false);
          }}
        />
      )}

      {/* Mobile Header — logo + utilitários; navegação principal fica na barra inferior.
          paddingTop = safe-area inset so content clears the status bar/notch
          (the app is edge-to-edge via viewport-fit=cover). */}
      <div
        className="md:hidden fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 bg-card border-b border-border"
        // Total height = 3.5rem content + the status-bar inset, EXACTLY matching
        // the <main> pt-[calc(3.5rem+env(safe-area-inset-top))] so there is no gap
        // between this fixed header and the page content below it.
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          minHeight: 'calc(3.5rem + env(safe-area-inset-top))',
        }}
      >
        {isInEditor ? (
          /* No editor a top bar é mínima (o pedido): ‹ voltar pra estante · o
             título do livro · o capítulo atual (algarismo romano). SEM logo,
             sem switcher de tema/idioma, sem sino — só o que faz sentido pra
             escrever; as demais opções vivem atrás da engrenagem à direita. */
          <button
            onClick={() => handleNavClick('projects')}
            className="flex min-w-0 flex-1 items-center gap-2 hover:opacity-80 transition-opacity"
            aria-label={t('bookDetail.backToShelf', 'Voltar para a estante')}
          >
            <ArrowLeft size={18} className="shrink-0 text-muted-foreground" />
            <span className="truncate font-serif text-sm font-medium text-foreground">
              {project?.metadata.title || t('projects.untitled', 'Sem título')}
            </span>
            {currentChapterRoman && (
              <span className="shrink-0 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                · {t('editorView.chapterAbbrev', 'Cap.')} {currentChapterRoman}
              </span>
            )}
          </button>
        ) : (
          <button
            onClick={() => handleNavClick('dashboard')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <img src="/log-black.png" alt="reformu.lat" className="h-4 dark:hidden" />
            <img src="/log-white.png" alt="reformu.lat" className="h-4 hidden dark:block" />
            <span className="font-mono text-sm font-bold text-foreground tracking-tight">
              reformu<span className="text-accent">.</span>lat
            </span>
          </button>
        )}
        <div className="flex shrink-0 items-center gap-0.5">
          {/* Tema/idioma/sino só FORA do editor — dentro dele a barra fica limpa,
              com todas as opções de escrita reunidas atrás da engrenagem. */}
          {!isInEditor && <ThemeSwitch sz={22} />}
          {!isInEditor && <LangSelect />}
          {!isInEditor && <NotificationsBell />}
          {isInEditor && (
            <EditorSettingsMenu
              settings={settings}
              updateSettings={updateSettings}
              onOpenTableOfContents={() => window.dispatchEvent(new Event('toggle-toc'))}
            />
          )}
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <Drawer open={mobileMenuOpen} onOpenChange={setMobileMenuOpen} direction="left">
        <DrawerContent
          className="flex w-72 flex-col border-border bg-card md:hidden"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <DrawerTitle className="sr-only">{t('appShell.menu', 'Menu')}</DrawerTitle>
          <>
              {/* Mobile sidebar header */}
              <div className="h-14 flex items-center justify-between px-4 border-b border-border">
                <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-bold text-foreground">
                    reformu<span className="text-accent">.</span>lat
                  </span>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 text-muted-foreground"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Mobile navigation */}
              <div className="flex-1 overflow-y-auto p-2">
                {isInEditor && project ? (
                  // Rich TOC — feature-parity with the desktop sidebar
                  <div className="space-y-3">
                    {/* Back to projects */}
                    <button
                      onClick={() => { navigate('/app/projects'); setMobileMenuOpen(false); }}
                      className="group flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono font-semibold uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors"
                    >
                      <ChevronRight size={12} className="rotate-180 transition-transform group-hover:-translate-x-0.5" />
                      {t('nav.backToProjects', 'Voltar para Projetos')}
                    </button>

                    {/* Project title */}
                    <div className="px-3 py-1">
                      <h2 className="font-semibold text-foreground truncate">
                        {project.metadata.title || t('projects.untitled', 'Sem título')}
                      </h2>
                    </div>

                    {project.kind !== 'manga' && (<>
                    {/* FRONT MATTER — fixed order, toggle via settings popup */}
                    <div className="rounded-lg p-1">
                      <div className="flex items-center justify-between px-1 py-1 mb-1">
                        <button
                          onClick={() => setExpandedSections((p) => ({ ...p, front: !p.front }))}
                          className="flex items-center gap-1.5 font-mono text-[8.5px] font-medium text-muted-foreground uppercase tracking-[0.24em] hover:text-foreground"
                        >
                          {expandedSections.front ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                          {t('appShell.frontMatter', 'Pré-Textuais')}
                          {frontMatter.length > 0 && (
                            <span className="ml-1 text-[9px] bg-muted px-1 rounded">{frontMatter.length}</span>
                          )}
                        </button>
                        <SectionFilterPopup
                          title={t('appShell.frontMatter', 'Pré-Textuais')}
                          types={FRONT_MATTER_TYPES}
                          enabledTypes={frontMatter.map((c) => c.sectionType)}
                          onToggle={handleToggleFrontMatter}
                        />
                      </div>
                      <AnimatePresence initial={false}>
                        {expandedSections.front && frontMatter.length > 0 && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                            className="overflow-hidden"
                          >
                            <div className="space-y-0.5">
                              {frontMatter.map((chapter) => (
                                <FixedChapterItem
                                  key={chapter.id}
                                  chapter={chapter}
                                  isActive={currentChapterId === chapter.id}
                                  onSelect={() => { setCurrentChapter(chapter.id); setMobileMenuOpen(false); }}
                                  onDelete={() => handleDeleteChapter(chapter.id)}
                                />
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                      {expandedSections.front && frontMatter.length === 0 && (
                        <p className="text-[10px] text-muted-foreground/60 px-2 py-2 text-center italic">
                          {t('appShell.clickIconToAdd', 'Clique no ícone para adicionar')}
                        </p>
                      )}
                    </div>

                    {/* BODY — draggable chapters */}
                    <div className="rounded-lg p-1">
                      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 px-1 py-1 mb-1">
                        <button
                          onClick={() => setExpandedSections((p) => ({ ...p, body: !p.body }))}
                          className="flex items-center gap-1.5 whitespace-nowrap font-mono text-[8.5px] font-medium text-muted-foreground uppercase tracking-[0.24em] hover:text-foreground"
                        >
                          {expandedSections.body ? <ChevronDown size={10} className="shrink-0" /> : <ChevronRight size={10} className="shrink-0" />}
                          <BookOpen size={10} className="shrink-0" />
                          {t('appShell.yourStory', 'Sua História')}
                          <span className="ml-1 text-[9px] bg-primary/20 px-1 rounded">{bodyContent.length}</span>
                        </button>
                        {expandedSections.body && (
                          <span className="whitespace-nowrap text-[9px] text-primary/50 font-medium">{t('appShell.dragToReorder', 'arraste para reordenar')}</span>
                        )}
                      </div>

                      <AnimatePresence initial={false}>
                        {expandedSections.body && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                            className="overflow-hidden"
                          >
                            <DndContext
                              sensors={sensors}
                              collisionDetection={closestCenter}
                              onDragEnd={(e) => handleDragEnd(e, bodyContent)}
                            >
                              <SortableContext
                                items={bodyContent.map((c) => c.id)}
                                strategy={verticalListSortingStrategy}
                              >
                                <div className="space-y-0.5">
                                  {(() => {
                                    let chapterNum = 0;
                                    return bodyContent.map((chapter) => {
                                      if (chapter.sectionType === 'chapter') chapterNum++;
                                      return (
                                        <SortableChapterItem
                                          key={chapter.id}
                                          chapter={chapter}
                                          chapterNumber={chapter.sectionType === 'chapter' ? chapterNum : undefined}
                                          isActive={currentChapterId === chapter.id}
                                          onSelect={() => { setCurrentChapter(chapter.id); setMobileMenuOpen(false); }}
                                          onDelete={() => handleDeleteChapter(chapter.id)}
                                          onRename={(title) => handleRenameChapter(chapter.id, title)}
                                          directActions
                                        />
                                      );
                                    });
                                  })()}
                                </div>
                              </SortableContext>
                            </DndContext>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* New chapter — opens the unified create modal (Vazio / Com IA).
                          Plain button avoids nested DropdownMenu issues inside vaul Drawer. */}
                      <button
                        type="button"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setNewChapterOpen(true);
                        }}
                        className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-primary/30 p-2.5 text-xs font-medium text-primary/70 transition-colors hover:border-primary hover:bg-primary/10 hover:text-primary touch-manipulation active:scale-[0.98]"
                      >
                        <Plus size={14} />
                        {t('editor.addChapter', 'Novo capítulo')}
                      </button>
                    </div>

                    {/* BACK MATTER — fixed order, toggle via settings popup */}
                    <div className="rounded-lg p-1">
                      <div className="flex items-center justify-between px-1 py-1 mb-1">
                        <button
                          onClick={() => setExpandedSections((p) => ({ ...p, back: !p.back }))}
                          className="flex items-center gap-1.5 font-mono text-[8.5px] font-medium text-muted-foreground uppercase tracking-[0.24em] hover:text-foreground"
                        >
                          {expandedSections.back ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                          {t('appShell.backMatter', 'Pós-Textuais')}
                          {backMatter.length > 0 && (
                            <span className="ml-1 text-[9px] bg-muted px-1 rounded">{backMatter.length}</span>
                          )}
                        </button>
                        <SectionFilterPopup
                          title={t('appShell.backMatter', 'Pós-Textuais')}
                          types={BACK_MATTER_TYPES}
                          enabledTypes={backMatter.map((c) => c.sectionType)}
                          onToggle={handleToggleBackMatter}
                        />
                      </div>
                      <AnimatePresence initial={false}>
                        {expandedSections.back && backMatter.length > 0 && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                            className="overflow-hidden"
                          >
                            <div className="space-y-0.5">
                              {backMatter.map((chapter) => (
                                <FixedChapterItem
                                  key={chapter.id}
                                  chapter={chapter}
                                  isActive={currentChapterId === chapter.id}
                                  onSelect={() => { setCurrentChapter(chapter.id); setMobileMenuOpen(false); }}
                                  onDelete={() => handleDeleteChapter(chapter.id)}
                                />
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                      {expandedSections.back && backMatter.length === 0 && (
                        <p className="text-[10px] text-muted-foreground/60 px-2 py-2 text-center italic">
                          {t('appShell.clickIconToAdd', 'Clique no ícone para adicionar')}
                        </p>
                      )}
                    </div>
                    </>)}
                  </div>
                ) : (
                  // "Mais" drawer — secondary destinations (primary items are in the bottom tab bar)
                  <nav className="space-y-1">
                    <p className="px-3 pt-1 pb-2 text-[10px] font-mono font-bold uppercase tracking-widest text-muted-foreground/60">
                      {t('appShell.moreSection', 'Mais')}
                    </p>
                    {moreNavItems.map((item) => (
                      <NavItem
                        key={item.id}
                        icon={item.icon}
                        label={item.label}
                        badge={item.badge}
                        active={currentView === item.id}
                        onClick={() => handleNavClick(item.id)}
                      />
                    ))}
                    {/* Planos */}
                    <button
                      onClick={() => { setUpgradeOpen(true); setMobileMenuOpen(false); }}
                      className="w-full p-2.5 rounded-xl text-xs font-mono font-medium flex items-center gap-3 border border-transparent text-muted-foreground hover:text-primary transition-all touch-manipulation"
                    >
                      <div className="w-4 h-4 flex items-center justify-center shrink-0">
                        <CreditCard size={16} />
                      </div>
                      <span className="flex-1 text-left">{t('nav.plans', 'Planos')}</span>
                    </button>
                  </nav>
                )}
              </div>

              {/* Mobile drawer — bottom: conta, config, sair */}
              <div className="p-3 border-t border-border space-y-1">
                <button
                  onClick={() => { handleNavClick('account'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                >
                  <User size={16} className="shrink-0" />
                  <span className="flex-1 text-left">{t('nav.account', 'Conta')}</span>
                </button>
                <button
                  onClick={() => { handleNavClick('settings'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                >
                  <Settings size={16} className="shrink-0" />
                  <span className="flex-1 text-left">{t('nav.settings', 'Configurações')}</span>
                </button>
                <div className="h-px bg-border my-1" />
                <button
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-destructive hover:bg-destructive/10 transition-colors"
                >
                  <LogOut size={16} className="shrink-0" />
                  <span className="flex-1 text-left">{t('auth.logout', 'Sair')}</span>
                </button>

                <div className="h-px bg-border my-1" />
                {/* User identifier */}
                <div className="flex items-center gap-2.5 px-3 py-2">
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-primary to-accent flex items-center justify-center text-primary-foreground text-xs font-bold shrink-0">
                    {userInitials}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-foreground truncate leading-none mb-1">{user?.name}</p>
                    <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-primary/10 border border-primary/20 text-primary leading-none">{plan}</span>
                  </div>
                </div>
              </div>
          </>
        </DrawerContent>
      </Drawer>

      {/* Main Content */}
      <main
        className={cn(
          // pt clears the fixed mobile header (56px) PLUS the status-bar safe area.
          'flex-1 flex flex-col min-w-0 overflow-hidden pt-[calc(3.5rem+env(safe-area-inset-top))] md:pt-0 md:pb-0',
          // On mobile: pad bottom to clear the fixed bottom tab bar + device safe area.
          // In editor (immersive), no bottom bar → no extra padding.
          hideMobileTabBar ? 'pb-0' : 'pb-[calc(env(safe-area-inset-bottom)+4rem)]'
        )}
      >
        {children}
      </main>

      {/* ── Mobile Bottom Tab Bar ──────────────────────────────────────────────
           Only shown on mobile (md:hidden). Hidden in editor (imersivo).
           Respects iOS/Android safe-area via paddingBottom CSS env().
           5 abas: Início · Livros · Criar · Mensagens · Mais               */}
      {!hideMobileTabBar && (
        <nav className="mtab-mock md:hidden fixed bottom-0 inset-x-0 z-50 backdrop-blur-sm">
            {/* Início */}
            <BottomTabButton
              icon={<LayoutDashboard size={19} />}
              label={t('nav.dashboard', 'Início')}
              active={currentView === 'dashboard'}
              onClick={() => handleNavClick('dashboard')}
            />

            {/* Estante */}
            <BottomTabButton
              icon={<GiBookshelf size={19} />}
              label={t('nav.projects', 'Estante')}
              active={currentView === 'projects'}
              onClick={() => handleNavClick('projects')}
            />

            {/* Escrever — orbe central: continua o último livro (ou abre o
                fluxo de criação quando a estante ainda está vazia) */}
            <button
              onClick={() => {
                const latest = [...projects].sort(
                  (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
                )[0];
                if (latest) {
                  setCurrentProject(latest.id);
                  navigate(`/app/editor/${latest.id}`);
                } else {
                  setCreateOpen(true);
                }
              }}
              className="flex-1 flex flex-col items-center justify-center gap-1 touch-manipulation active:scale-95 transition-transform duration-150"
              aria-label={t('estante.write', 'Escrever')}
            >
              <div
                className="orb-pulse w-12 h-12 rounded-full flex items-center justify-center -mt-5"
                style={{
                  background: 'radial-gradient(circle at 36% 30%, var(--ember-hot), var(--ember) 55%, #C08A2E)',
                  boxShadow: '0 0 0 4px var(--background), 0 6px 22px -4px rgba(223,201,147,0.6), 0 0 30px -6px rgba(223,201,147,0.5)',
                }}
              >
                <GiQuill size={19} style={{ color: '#141005' }} />
              </div>
              <span className="text-[10px] font-medium text-muted-foreground leading-none">
                {t('estante.write', 'Escrever')}
              </span>
            </button>

            {/* Mensagens — bar is only hidden once a conversation thread is
                open (see isInOpenConversation above); visibly active on the
                conversation list. Cast keeps the type honest. */}
            <BottomTabButton
              icon={<GiEnvelope size={19} />}
              label={t('nav.messages', 'Mensagens')}
              active={(currentView as string) === 'messages'}
              onClick={() => handleNavClick('messages')}
              badge={unreadCount}
            />

            {/* Mais — abre o drawer com navegação secundária */}
            <BottomTabButton
              icon={<Menu size={19} />}
              label={t('nav.more', 'Mais')}
              active={mobileMenuOpen}
              onClick={() => setMobileMenuOpen((o) => !o)}
            />
        </nav>
      )}
      </div>
    </div>
  );
}

/** Numeral romano — os capítulos do sumário são "XII · Título", como no mock. */
function tocRoman(n: number): string {
  if (n <= 0 || n >= 4000) return String(n);
  const tbl: [number, string][] = [[1000,'M'],[900,'CM'],[500,'D'],[400,'CD'],[100,'C'],[90,'XC'],[50,'L'],[40,'XL'],[10,'X'],[9,'IX'],[5,'V'],[4,'IV'],[1,'I']];
  let out = ''; let v = n;
  for (const [val, sym] of tbl) { while (v >= val) { out += sym; v -= val; } }
  return out;
}

// Fixed Chapter Item (for front/back matter - no drag)
function FixedChapterItem({
  chapter,
  isActive,
  onSelect,
  onDelete,
}: {
  chapter: Chapter;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const Icon = sectionIcons[chapter.sectionType] || FileText;

  void Icon; // ícone não aparece no visual do mock (linha limpa)
  return (
    <div
      className={cn(
        'group relative flex items-center gap-2 w-full min-w-0 px-[10px] py-[7px] rounded-lg text-left transition-colors duration-150',
        isActive
          ? 'chapter-on bg-[rgba(223,201,147,0.08)] text-[color:var(--ember-hot)]'
          : 'hover:bg-foreground/5 text-foreground/80'
      )}
    >
      <button onClick={onSelect} className="flex-1 min-w-0 text-left">
        <span className="block text-[12px] leading-snug truncate">
          {chapter.title || sectionLabels[chapter.sectionType]}
        </span>
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover:opacity-100 text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10 transition-all touch-manipulation"
        title={t('appShell.delete', 'Excluir')}
      >
        <X size={12} />
      </button>
    </div>
  );
}

// Section Filter Popup - Uses Portal pattern for proper z-index
function SectionFilterPopup({
  title,
  types,
  enabledTypes,
  onToggle,
}: {
  title: string;
  types: SectionType[];
  enabledTypes: SectionType[];
  onToggle: (type: SectionType) => void;
}) {
  const { t } = useTranslation();
  return (
    <Popover>
      <PopoverTrigger
        className="rounded bg-background p-1 text-muted-foreground transition-colors hover:bg-accent/20 hover:text-foreground"
        title={t('appShell.configurePages', 'Configurar páginas')}
      >
        <Settings2 size={12} />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[260px] p-3">
        <h3 className="mb-3 text-sm font-semibold">{title}</h3>
        <div className="max-h-[50vh] space-y-1 overflow-y-auto">
          {types.map((type) => {
            const Icon = sectionIcons[type];
            const isEnabled = enabledTypes.includes(type);
            return (
              <button
                key={type}
                onClick={() => onToggle(type)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-lg p-2 text-sm transition-colors',
                  isEnabled ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'
                )}
              >
                <div
                  className={cn(
                    'flex h-4 w-4 items-center justify-center rounded border',
                    isEnabled ? 'border-primary bg-primary' : 'border-border'
                  )}
                >
                  {isEnabled && <Check size={10} className="text-primary-foreground" />}
                </div>
                <Icon size={14} />
                <span>{sectionLabels[type]}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Sortable Chapter Item component
function SortableChapterItem({
  chapter,
  chapterNumber,
  isActive,
  onSelect,
  onDelete,
  onRename,
  directActions = false,
}: {
  chapter: Chapter;
  chapterNumber?: number;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onRename: (title: string) => void;
  /**
   * When true, renders rename and delete as always-visible direct buttons
   * instead of a DropdownMenu — required in the mobile vaul Drawer where
   * nested DropdownMenus do not open on touch.
   */
  directActions?: boolean;
}) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(chapter.title);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: chapter.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const Icon = sectionIcons[chapter.sectionType] || FileText;

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSaveRename = () => {
    if (editTitle.trim() && editTitle !== chapter.title) {
      onRename(editTitle.trim());
    } else {
      setEditTitle(chapter.title);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveRename();
    } else if (e.key === 'Escape') {
      setEditTitle(chapter.title);
      setIsEditing(false);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group relative flex items-center gap-2 rounded-lg px-[10px] transition-colors duration-150 min-w-0',
        isDragging && 'opacity-50 z-50',
        isActive
          ? 'chapter-on bg-[rgba(223,201,147,0.08)]'
          : 'hover:bg-foreground/5'
      )}
    >
      {/* Drag handle — sobreposto no hover, não desloca a linha */}
      <button
        {...attributes}
        {...listeners}
        className="absolute -left-1 top-1/2 -translate-y-1/2 p-1 cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground touch-none opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <GripVertical size={13} />
      </button>

      {/* Chapter content */}
      {isEditing ? (
        <div className="flex-1 flex items-center gap-1 pr-2">
          <input
            ref={inputRef}
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onBlur={handleSaveRename}
            onKeyDown={handleKeyDown}
            className="flex-1 text-sm bg-background border border-border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <button
            onClick={handleSaveRename}
            className="p-1 hover:bg-[rgba(94,234,212,0.12)] rounded" style={{ color: 'var(--aurora)' }}
          >
            <Check size={14} />
          </button>
        </div>
      ) : (
        <button
          onClick={onSelect}
          className={cn(
            'flex-1 text-left py-[7px] text-[12px] leading-snug transition-colors flex items-baseline gap-1.5 min-w-0',
            isActive ? 'text-[color:var(--ember-hot)]' : 'text-foreground/80'
          )}
        >
          {chapterNumber ? (
            <span className="shrink-0">{tocRoman(chapterNumber)} ·</span>
          ) : (
            <Icon size={12} className="flex-shrink-0 opacity-60 self-center" />
          )}
          <span className="truncate">{chapter.title}</span>
        </button>
      )}

      {/* Word count — only shown in desktop mode; hidden on hover to reveal the menu.
          In directActions mode the action buttons are always visible, so we skip it. */}
      {!isEditing && !directActions && (
        <span className="font-mono text-[8px] text-muted-foreground shrink-0 group-hover:opacity-0 transition-opacity">
          {chapter.wordCount > 0 ? chapter.wordCount.toLocaleString('pt-BR') : ''}
        </span>
      )}

      {/* Actions — DropdownMenu on desktop, always-visible buttons in mobile drawer */}
      {!isEditing && (
        directActions ? (
          // Direct controls: always visible tap targets — safe inside a vaul Drawer.
          <div className="flex shrink-0 items-center gap-0.5">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
              className="rounded p-1.5 text-muted-foreground/50 transition-colors hover:bg-muted hover:text-muted-foreground touch-manipulation active:bg-muted"
              title={t('appShell.rename', 'Renomear')}
            >
              <Pencil size={14} />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="rounded p-1.5 text-muted-foreground/50 transition-colors hover:bg-destructive/10 hover:text-destructive touch-manipulation active:bg-destructive/10"
              title={t('appShell.delete', 'Excluir')}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ) : (
          // Desktop: DropdownMenu works correctly outside a vaul Drawer.
          <DropdownMenu>
            <DropdownMenuTrigger className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 text-muted-foreground/60 transition-all hover:bg-muted hover:text-muted-foreground data-[state=open]:bg-muted data-[state=open]:text-muted-foreground touch-manipulation">
              <MoreHorizontal size={13} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-32">
              <DropdownMenuItem onClick={() => setIsEditing(true)}>
                <Pencil size={14} />
                {t('appShell.rename', 'Renomear')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onDelete}
                className="text-destructive data-[highlighted]:text-destructive"
              >
                <Trash2 size={14} />
                {t('appShell.delete', 'Excluir')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      )}
    </div>
  );
}

// NavItem component — estilo moonlight: font-mono, borda, ativo/inativo
function NavItem({
  icon,
  label,
  active,
  badge,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  active?: boolean;
  badge?: number;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn('rail-i touch-manipulation relative', active && 'on')}
      style={active ? { background: 'transparent' } : undefined}
    >
      {active && (
        <motion.span
          layoutId="rail-pill"
          className="rail-pill-bg absolute inset-0 rounded-[10px]"
          transition={{ type: 'spring', stiffness: 420, damping: 34 }}
        />
      )}
      <div className="relative z-10 w-4 h-4 flex items-center justify-center shrink-0">{icon}</div>
      <span className="relative z-10 flex-1 text-left">{label}</span>
      {badge !== undefined && badge > 0 && <span className="n relative z-10">{badge}</span>}
    </button>
  );
}

// Item of the user popup menu in the sidebar
function UserMenuItem({
  icon,
  label,
  onClick,
  destructive,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors',
        destructive
          ? 'text-destructive hover:bg-destructive/10'
          : 'text-foreground hover:bg-muted'
      )}
    >
      <span className="flex h-4 w-4 items-center justify-center shrink-0">{icon}</span>
      <span className="flex-1">{label}</span>
    </button>
  );
}
